import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Drawer,
  Typography,
  IconButton,
  Box,
  Tooltip,
  CircularProgress,
  TextField,
  InputAdornment,
  Dialog,
  DialogContent,
  DialogActions,
  Button,
} from "@material-ui/core";
import {
  Close as CloseIcon,
  Search as SearchIcon,
  ArrowBack as ArrowBackIcon,
  ChevronRight as ChevronRightIcon,
  ExpandMore as ExpandMoreIcon,
  Call as CallIcon,
  Email as EmailIcon,
  Settings as SettingsIcon,
} from "@material-ui/icons";
import HelpCenterIcon from "../../assets/ais_new_images/HelpCenter.svg";
import { PermissionControllerAction } from "../../actions/PermissionControllerAction";
import { useNotificationStyles } from "../NotificationDrawer/notificationStyles";

const getSupportContactInfo = () => {
  try {
    const rawConfig = localStorage.getItem("config");
    if (!rawConfig) return { number: "", email: "" };
    const parsedConfig = JSON.parse(rawConfig);
    let datas = parsedConfig?.datas;
    if (typeof datas === "string") {
      datas = JSON.parse(datas);
    }
    const support = datas?.support || {};
    return {
      number: support.number || "",
      email: support.email || datas?.emailIdForNotifyUserVehicle || "",
    };
  } catch (err) {
    console.error("Error reading support contact info from config", err);
    return { number: "", email: "" };
  }
};

const TypewriterText = ({ text, speed = 15, style }) => {
  const [displayedText, setDisplayedText] = useState("");

  useEffect(() => {
    setDisplayedText(""); // reset when text changes
    if (!text) return;

    let index = 0;
    const intervalId = setInterval(() => {
      index += 1;
      setDisplayedText(text.slice(0, index));
      if (index >= text.length) {
        clearInterval(intervalId);
      }
    }, speed);

    return () => clearInterval(intervalId);
  }, [text, speed]);

  return (
    <Typography style={style}>
      {displayedText}
      {displayedText.length < (text?.length || 0) && (
        <span className="typewriter-cursor">|</span>
      )}
    </Typography>
  );
};

const FaqDrawer = () => {
  const classes = useNotificationStyles();
  const [open, setOpen] = useState(false);

  // FAQ Categories State
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);

  // Navigation State
  const [selectedCategory, setSelectedCategory] = useState(null);

  // Questions State
  const [questions, setQuestions] = useState([]);
  const [loadingQuestions, setLoadingQuestions] = useState(false);

  // Answers State
  const [expandedQuestionId, setExpandedQuestionId] = useState(null);
  const [answersMap, setAnswersMap] = useState({});
  console.log("answersMap",answersMap)
  const [loadingAnswerMap, setLoadingAnswerMap] = useState({});

  // Search State
  const [searchQuery, setSearchQuery] = useState("");

  // Fetch FAQ Categories when Drawer opens
  const fetchCategories = useCallback(async () => {
    setLoadingCategories(true);
    try {
      const res = await PermissionControllerAction.getAllFaqCategory();
      let list = [];
      if (res) {
        const code = res.responseCode ?? res.data?.responseCode;
        const payload = res.data ?? res;

        if ((code === 200 || !code) && Array.isArray(payload)) {
          list = payload;
        } else if (code === 200 && Array.isArray(res.data?.data)) {
          list = res.data.data;
        }
      }
      setCategories(list);
    } catch (err) {
      console.error("Error fetching FAQ categories:", err);
      setCategories([]);
    } finally {
      setLoadingCategories(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      fetchCategories();
    } else {
      // Reset state on drawer close
      setSelectedCategory(null);
      setQuestions([]);
      setExpandedQuestionId(null);
      setSearchQuery("");
    }
  }, [open, fetchCategories]);

  // Fetch Questions when a category is selected
  const handleCategoryClick = useCallback(async (category) => {
    setSelectedCategory(category);
    setSearchQuery("");
    setQuestions([]);
    setExpandedQuestionId(null);
    setLoadingQuestions(true);

    try {
      const res = await PermissionControllerAction.getAllFaqQuestions(category.id);
      let list = [];
      if (res) {
        const code = res.responseCode ?? res.data?.responseCode;
        const payload = res.data ?? res;

        if ((code === 200 || !code) && Array.isArray(payload)) {
          list = payload;
        } else if (code === 200 && Array.isArray(res.data?.data)) {
          list = res.data.data;
        }
      }
      setQuestions(list);
    } catch (err) {
      console.error("Error fetching FAQ questions:", err);
      setQuestions([]);
    } finally {
      setLoadingQuestions(false);
    }
  }, []);

  // Fetch Answer when a question is clicked / expanded
  const handleQuestionClick = useCallback(async (q) => {
    const questionId = q.id;

    if (expandedQuestionId === questionId) {
      setExpandedQuestionId(null);
      return;
    }

    setExpandedQuestionId(questionId);

    // Fetch if not already in answersMap
    if (!answersMap[questionId]) {
      setLoadingAnswerMap((prev) => ({ ...prev, [questionId]: true }));
      try {
        const res = await PermissionControllerAction.getAllFaqAnswers(questionId);
        
        let answerObj = { success: false, text: "No answers found for the given question." };

        if (res) {
          const responseCode = res.responseCode ?? res.data?.responseCode;
          const msg = res.message || res.data?.message || "No answers found for the given question.";
          const payload = res.data ?? res;

          if (responseCode === 200) {
            let items = [];
            if (Array.isArray(payload)) {
              items = payload;
            } else if (Array.isArray(res.data?.data)) {
              items = res.data.data;
            } else if (payload && typeof payload === "object") {
              items = [payload];
            }

            if (items.length > 0) {
              const textStr = items
                .map((item) => item.name || item.answer || item.answerText || item.description)
                .filter(Boolean)
                .join("\n\n");

              answerObj = {
                success: true,
                text: textStr || msg,
              };
            } else {
              answerObj = {
                success: false,
                text: msg,
              };
            }
          } else {
            // 404, 400 or other non-200 responseCode
            answerObj = {
              success: false,
              text: msg,
            };
          }
        }

        setAnswersMap((prev) => ({ ...prev, [questionId]: answerObj }));
      } catch (err) {
        console.error("Error fetching FAQ answer:", err);
        setAnswersMap((prev) => ({
          ...prev,
          [questionId]: {
            success: false,
            text: "No answers found for the given question.",
          },
        }));
      } finally {
        setLoadingAnswerMap((prev) => ({ ...prev, [questionId]: false }));
      }
    }
  }, [expandedQuestionId, answersMap]);

  // Support Contacts
  const contactInfo = useMemo(() => getSupportContactInfo(), []);

  // Contact confirmation dialog state
  const [contactDialog, setContactDialog] = useState({ open: false, type: null });

  const handleCallUs = () => {
    if (contactInfo.number) {
      setContactDialog({ open: true, type: "phone" });
    } else {
      alert("Support phone number is not configured.");
    }
  };

  const handleEmailUs = () => {
    if (contactInfo.email) {
      setContactDialog({ open: true, type: "email" });
    } else {
      alert("Support email address is not configured.");
    }
  };

  const handleContactConfirm = () => {
    if (contactDialog.type === "email") {
      const gmailComposeUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(contactInfo.email)}`;
      window.open(gmailComposeUrl, "_blank", "noopener,noreferrer");
    } else if (contactDialog.type === "phone") {
      window.location.href = `tel:${contactInfo.number}`;
    }
    setContactDialog({ open: false, type: null });
  };

  const handleContactCancel = () => {
    setContactDialog({ open: false, type: null });
  };
  // Filter Categories by search
  const filteredCategories = useMemo(() => {
    if (!searchQuery.trim()) return categories;
    const q = searchQuery.toLowerCase();
    return categories.filter((c) => {
      const name = (c.name || c.categoryName || c.title || "").toLowerCase();
      return name.includes(q);
    });
  }, [categories, searchQuery]);

  // Filter Questions by search
  const filteredQuestions = useMemo(() => {
    if (!searchQuery.trim()) return questions;
    const q = searchQuery.toLowerCase();
    return questions.filter((item) => {
      const text = (item.name || item.question || item.title || "").toLowerCase();
      return text.includes(q);
    });
  }, [questions, searchQuery]);

  return (
    <>
      <Tooltip title="Help Center & FAQs" arrow>
        <IconButton
          onClick={() => setOpen(true)}
          style={{ color: "#003a6b", padding: 8 }}
          size="small"
        >
          <img src={HelpCenterIcon} alt="Help Center" style={{ width: 26, height: 26 }} />
        </IconButton>
      </Tooltip>

      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        classes={{ paper: classes.drawerPaper }}
        transitionDuration={300}
      >
        {/* Animated Drawer Header (Matches Notification Drawer header) */}
        <div className={classes.header}>
          <div className={classes.headerContent}>
            <Box style={{ display: "flex", alignItems: "center", gap: 10 }}>
              {selectedCategory ? (
                <IconButton
                  size="small"
                  onClick={() => {
                    setSelectedCategory(null);
                    setSearchQuery("");
                  }}
                  style={{ color: "#ffffff", padding: 4 }}
                >
                  <ArrowBackIcon style={{ fontSize: 22 }} />
                </IconButton>
              ) : (
                <SettingsIcon style={{ fontSize: 20, color: "#ffffff" }} />
              )}
              <Typography className={classes.title}>
                {selectedCategory
                  ? selectedCategory.name || selectedCategory.categoryName || selectedCategory.title
                  : "Help Center"}
              </Typography>
            </Box>

            <IconButton
              size="small"
              onClick={() => setOpen(false)}
              style={{ color: "#ffffff", padding: 4 }}
            >
              <CloseIcon style={{ fontSize: 20 }} />
            </IconButton>
          </div>
        </div>

        {/* Search Bar */}
        <Box style={{ padding: "14px 20px 10px 20px", background: "#ffffff", borderBottom: "1px solid #e8eeff" }}>
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            placeholder={
              selectedCategory
                ? "Search questions..."
                : "Search categories..."
            }
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: "#94a3b8", fontSize: 20 }} />
                </InputAdornment>
              ),
              style: {
                borderRadius: 10,
                fontSize: 13.5,
                background: "#f8fafc",
              },
            }}
          />
        </Box>

        {/* Drawer Body Area */}
        <Box
          style={{
            flex: 1,
            overflowY: "auto",
            padding: "20px",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {!selectedCategory ? (
            /* SCREEN 1: HELP CENTER HOME / CATEGORIES LIST */
            <Box>
              {/* How can we help you section */}
              <Box style={{ marginBottom: 20 }}>
                <Typography
                  style={{
                    fontSize: 16,
                    fontWeight: 700,
                    color: "#0f172a",
                    marginBottom: 4,
                  }}
                >
                  How can we help you?
                </Typography>
                <Typography style={{ fontSize: 13, color: "#64748b" }}>
                  Browse our FAQs or reach out to support directly
                </Typography>
              </Box>

              {/* Call Us & Email Us Action Cards */}
              <Box style={{ display: "flex", gap: 14, marginBottom: 24 }}>
                {/* Call Us Card */}
                <Box
                  onClick={handleCallUs}
                  style={{
                    flex: 1,
                    background: "#ffffff",
                    border: "1.5px solid #e2e8f0",
                    borderRadius: 14,
                    padding: "18px 14px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    boxShadow: "0 2px 6px rgba(0,0,0,0.03)",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = "#0284c7";
                    e.currentTarget.style.boxShadow = "0 6px 16px rgba(2, 132, 199, 0.12)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "#e2e8f0";
                    e.currentTarget.style.boxShadow = "0 2px 6px rgba(0,0,0,0.03)";
                  }}
                >
                  <Box
                    style={{
                      width: 46,
                      height: 46,
                      borderRadius: "50%",
                      background: "#e0f2fe",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#0284c7",
                      marginBottom: 8,
                    }}
                  >
                    <CallIcon style={{ fontSize: 24 }} />
                  </Box>
                  <Typography
                    style={{
                      fontSize: 14,
                      fontWeight: 700,
                      color: "#0f172a",
                    }}
                  >
                    Call Us
                  </Typography>
                </Box>

                {/* Email Us Card */}
                <Box
                  onClick={handleEmailUs}
                  style={{
                    flex: 1,
                    background: "#ffffff",
                    border: "1.5px solid #e2e8f0",
                    borderRadius: 14,
                    padding: "18px 14px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    boxShadow: "0 2px 6px rgba(0,0,0,0.03)",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = "#475569";
                    e.currentTarget.style.boxShadow = "0 6px 16px rgba(71, 85, 105, 0.12)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "#e2e8f0";
                    e.currentTarget.style.boxShadow = "0 2px 6px rgba(0,0,0,0.03)";
                  }}
                >
                  <Box
                    style={{
                      width: 46,
                      height: 46,
                      borderRadius: "50%",
                      background: "#f1f5f9",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#475569",
                      marginBottom: 8,
                    }}
                  >
                    <EmailIcon style={{ fontSize: 24 }} />
                  </Box>
                  <Typography
                    style={{
                      fontSize: 14,
                      fontWeight: 700,
                      color: "#0f172a",
                    }}
                  >
                    Email Us
                  </Typography>
                </Box>
              </Box>

              {/* Frequently Asked Questions Section Title */}
              <Typography
                style={{
                  fontSize: 15,
                  fontWeight: 700,
                  color: "#0f172a",
                  marginBottom: 14,
                }}
              >
                Frequently Asked Questions
              </Typography>

              {/* Categories List */}
              {loadingCategories ? (
                <Box style={{ textAlign: "center", padding: "40px 0" }}>
                  <CircularProgress size={32} style={{ color: "#082D6B" }} />
                </Box>
              ) : filteredCategories.length === 0 ? (
                <Box
                  style={{
                    textAlign: "center",
                    padding: "40px 20px",
                    background: "#ffffff",
                    borderRadius: 14,
                    border: "1px solid #e2e8f0",
                  }}
                >
                  <Typography style={{ fontSize: 14, color: "#64748b" }}>
                    No categories found
                  </Typography>
                </Box>
              ) : (
                filteredCategories.map((cat) => {
                  const catName = cat.name || cat.categoryName || cat.title || "Category";

                  return (
                    <Box
                      key={cat.id}
                      onClick={() => handleCategoryClick(cat)}
                      style={{
                        background: "#ffffff",
                        border: "1.5px solid #e0e8f0",
                        borderRadius: 14,
                        padding: "16px 20px",
                        marginBottom: 12,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.03)",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = "#082D6B";
                        e.currentTarget.style.background = "#f8fafc";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = "#e0e8f0";
                        e.currentTarget.style.background = "#ffffff";
                      }}
                    >
                      <Typography
                        style={{
                          fontSize: 16,
                          fontWeight: 700,
                          color: "#0f172a",
                        }}
                      >
                        {catName}
                      </Typography>
                      <ChevronRightIcon style={{ color: "#78909c", fontSize: 22 }} />
                    </Box>
                  );
                })
              )}
            </Box>
          ) : (
            /* SCREEN 2: QUESTIONS LIST UNDER SELECTED CATEGORY */
            <Box>
              {loadingQuestions ? (
                <Box style={{ textAlign: "center", padding: "40px 0" }}>
                  <CircularProgress size={32} style={{ color: "#082D6B" }} />
                </Box>
              ) : filteredQuestions.length === 0 ? (
                <Box
                  style={{
                    textAlign: "center",
                    padding: "40px 20px",
                    background: "#ffffff",
                    borderRadius: 14,
                    border: "1px solid #e2e8f0",
                  }}
                >
                  <Typography style={{ fontSize: 14, color: "#64748b" }}>
                    No questions found in this category
                  </Typography>
                </Box>
              ) : (
                filteredQuestions.map((q) => {
                  const isExpanded = expandedQuestionId === q.id;
                  const isLoadingAns = loadingAnswerMap[q.id];
                  const qText = q.name || q.question || q.title || "Question";

                  return (
                    <Box
                      key={q.id}
                      style={{
                        background: "#ffffff",
                        border: isExpanded ? "1.5px solid #082D6B" : "1.5px solid #e0e8f0",
                        borderRadius: 14,
                        padding: "16px 18px",
                        marginBottom: 12,
                        transition: "all 0.2s ease",
                        boxShadow: isExpanded
                          ? "0 4px 14px rgba(8, 45, 107, 0.10)"
                          : "0 1px 3px rgba(0,0,0,0.03)",
                      }}
                    >
                      {/* Question Header / Click trigger */}
                      <Box
                        onClick={() => handleQuestionClick(q)}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          cursor: "pointer",
                          gap: 12,
                        }}
                      >
                        <Typography
                          style={{
                            fontSize: 14.5,
                            fontWeight: 700,
                            color: "#0f172a",
                            lineHeight: 1.45,
                            flex: 1,
                          }}
                        >
                          {qText}
                        </Typography>
                        {isExpanded ? (
                          <ExpandMoreIcon style={{ color: "#082D6B", fontSize: 22 }} />
                        ) : (
                          <ChevronRightIcon style={{ color: "#78909c", fontSize: 22 }} />
                        )}
                      </Box>

                      {/* Answer Expanded View */}
                      {isExpanded && (
                        <Box
                          style={{
                            marginTop: 12,
                            paddingTop: 12,
                            borderTop: "1px dashed #cbd5e1",
                          }}
                        >
                          {isLoadingAns ? (
                            <Box style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0" }}>
                              <CircularProgress size={18} style={{ color: "#082D6B" }} />
                              <Typography style={{ fontSize: 13, color: "#64748b" }}>
                                Loading answer...
                              </Typography>
                            </Box>
                          ) : (
                            <Box
                              style={{
                                background: answersMap[q.id]?.success ? "#f8fafc" : "#fff5f5",
                                border: answersMap[q.id]?.success ? "1px solid #e2e8f0" : "1px solid #fecaca",
                                borderRadius: 10,
                                padding: "14px 16px",
                              }}
                            >
                              {/* <Typography
                                style={{
                                  fontSize: 13.5,
                                  color: answersMap[q.id]?.success ? "#334155" : "#b91c1c",
                                  lineHeight: 1.6,
                                  whiteSpace: "pre-line",
                                }}
                              >
                                {answersMap[q.id]?.text || "No answers found for the given question."}
                              </Typography> */}

                              <TypewriterText
                                text={answersMap[q.id]?.text || "No answers found for the given question."}
                                speed={25}
                                style={{
                                  fontSize: 13.5,
                                  color: answersMap[q.id]?.success ? "#334155" : "#b91c1c",
                                  lineHeight: 1.6,
                                  whiteSpace: "pre-line",
                                }}
                              />
                            </Box>
                          )}
                        </Box>
                      )}
                    </Box>
                  );
                })
              )}
            </Box>
          )}
        </Box>

        {/* Drawer Footer */}
        {/* <Box
          style={{
            padding: "12px 20px",
            background: "#ffffff",
            borderTop: "1px solid #f1f5f9",
            textAlign: "center",
          }}
        >
          <Typography style={{ fontSize: 12, color: "#94a3b8" }}>
            App Version: v{config.version || "2.59"}
          </Typography>
        </Box> */}
      </Drawer>

      {/* Contact Confirmation Dialog */}
      <Dialog
        open={contactDialog.open}
        onClose={handleContactCancel}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          style: {
            borderRadius: 16,
            boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
            overflow: "hidden",
          },
        }}
      >
        <div
          style={{
            background: contactDialog.type === "email"
              ? "linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%)"
              : "linear-gradient(135deg, #064e3b 0%, #059669 100%)",
            padding: "20px 24px",
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}
        >
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              background: "rgba(255,255,255,0.15)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {contactDialog.type === "email" ? (
              <EmailIcon style={{ color: "#fff", fontSize: 22 }} />
            ) : (
              <CallIcon style={{ color: "#fff", fontSize: 22 }} />
            )}
          </div>
          <Typography style={{ fontWeight: 700, fontSize: 17, color: "#fff" }}>
            {contactDialog.type === "email" ? "Email Us" : "Call Us"}
          </Typography>
        </div>
        <DialogContent style={{ padding: "24px", background: "#fafbfc" }}>
          <Typography style={{ fontSize: 14, color: "#334155", lineHeight: 1.6 }}>
            If you want to connect with us, {contactDialog.type === "email" ? "reach us at the following email" : "call us at the following number"}:
          </Typography>
          <Box
            style={{
              marginTop: 14,
              padding: "12px 16px",
              background: "#fff",
              border: "1.5px solid #e2e8f0",
              borderRadius: 10,
              display: "flex",
              alignItems: "center",
              gap: 10,
            }}
          >
            {contactDialog.type === "email" ? (
              <EmailIcon style={{ color: "#2563eb", fontSize: 20 }} />
            ) : (
              <CallIcon style={{ color: "#059669", fontSize: 20 }} />
            )}
            <Typography style={{ fontSize: 15, fontWeight: 700, color: "#0f172a", wordBreak: "break-all" }}>
              {contactDialog.type === "email" ? contactInfo.email : contactInfo.number}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions
          style={{
            padding: "14px 24px 20px",
            gap: 10,
            background: "#fafbfc",
            borderTop: "1px solid #eef1f5",
          }}
        >
          <Button
            onClick={handleContactCancel}
            variant="outlined"
            style={{
              borderColor: "#d0d5dd",
              color: "#475467",
              minWidth: 90,
              borderRadius: 10,
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleContactConfirm}
            variant="contained"
            style={{
              minWidth: 140,
              borderRadius: 10,
              textTransform: "none",
              fontWeight: 600,
              color: "#fff",
              background: contactDialog.type === "email"
                ? "linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%)"
                : "linear-gradient(135deg, #064e3b 0%, #059669 100%)",
              boxShadow: contactDialog.type === "email"
                ? "0 4px 12px rgba(37,99,235,0.3)"
                : "0 4px 12px rgba(5,150,105,0.3)",
            }}
          >
            Connect with Us
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FaqDrawer;
